/**
 * Returns a display name for a contact, never "Unknown".
 * Priority: firstName + lastName → email prefix (capitalized) → fallback string.
 *
 * @param firstName  Contact's first name (may be empty/null)
 * @param lastName   Contact's last name (may be empty/null)
 * @param email      Contact's primary email (used as fallback)
 * @param fallback   Last-resort string (defaults to empty string so callers can decide)
 */
export function getDisplayName(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  email?: string | null,
  fallback = "",
): string {
  const full = [firstName, lastName]
    .filter((s): s is string => !!s && s.toUpperCase() !== "N/A")
    .join(" ")
    .trim();
  if (full) return full;

  if (email) {
    const prefix = email.split("@")[0];
    if (prefix) {
      // Convert "john.doe" or "john_doe" to "John Doe"
      return prefix
        .replace(/[._-]/g, " ")
        .replace(/(^\w|\s\w)/g, (m) => m.toUpperCase());
    }
  }

  return fallback;
}

/**
 * Extract a sender display name from the email "From" header (metadataFrom).
 * Matches the reference implementation (coffee-ui): extracts the email address
 * from the raw header, then derives a readable name from the username part.
 *
 * @param metadataFrom  Raw "From" header, e.g. "John Doe <john.doe@co.com>" or "john.doe@co.com"
 * @param fallback      Last-resort string if no email can be extracted
 */
export function getSenderFromMetadata(
  metadataFrom: string | null | undefined,
  fallback = "Unknown Contact",
): string {
  if (!metadataFrom) return fallback;
  const emailMatch = metadataFrom.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-z]{2,}/i);
  if (!emailMatch) return fallback;
  return getDisplayName(null, null, emailMatch[0], fallback);
}
